import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCalendarCheck,
  faChartLine,
  faCommentDots,
  faEye,
  faFlag,
  faImage,
  faStar,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import Spinner from "react-activity/dist/Spinner";
import "react-activity/dist/Spinner.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import { apiUrl } from "../../config/Config";
import { formatIstDate, formatIstDateTime } from "../../utils/dateTime";
import PostPreviewModal from "../common/PostPreviewModal";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showloding, setshowloding] = useState(false);

  const headers = useMemo(() => ({
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + userdata?.token,
    },
  }), [userdata?.token]);

  const handleInvalidUser = useCallback((message) => {
    if (message === "invalid user") {
      dispatch({ type: "userdata", payload: {} });
      navigate("/admin/login");
      return true;
    }
    return false;
  }, [dispatch, navigate]);

  const fetchDashboard = useCallback(async () => {
    if (!userdata || Object.keys(userdata).length === 0) {
      navigate("/admin/login");
      return;
    }

    setshowloding(true);
    await axios
      .get(apiUrl + "getadmindashboard", headers)
      .then((res) => {
        if (res.data.status === "success") {
          setDashboardData(res.data.data);
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        alert(err.message);
      });
    setshowloding(false);
  }, [handleInvalidUser, headers, navigate, userdata]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const kpis = dashboardData?.kpis || {};
  const daily = dashboardData?.daily || {};
  const engagement = dashboardData?.engagement || {};
  const moderation = dashboardData?.moderation || {};
  const topRegions = dashboardData?.top_regions || [];
  const latestPosts = dashboardData?.latest_posts || [];
  const recentReports = dashboardData?.recent_reports || [];
  const recentFeedback = dashboardData?.recent_feedback || [];

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString("en-IN");
  };

  const kpiCards = [
    {
      title: "Total Users",
      value: kpis.total_users,
      trend: `${formatNumber(kpis.new_users_today)} joined today`,
      icon: faUserGroup,
      color: "#EAF7F0",
      accent: "#198754",
    },
    {
      title: "Published Posts",
      value: kpis.total_posts,
      trend: `${formatNumber(kpis.new_posts_today)} new today`,
      icon: faImage,
      color: "#FFF4DE",
      accent: "#D97706",
    },
    {
      title: "Event Enrollments",
      value: kpis.total_event_enrollments,
      trend: `${formatNumber(kpis.event_enrollments_today)} today`,
      icon: faCalendarCheck,
      color: "#EEF2FF",
      accent: "#4F46E5",
    },
    {
      title: "Pending Reports",
      value: kpis.pending_reports,
      trend: `${formatNumber(kpis.blocked_users)} blocked pairs`,
      icon: faFlag,
      color: "#FEECEC",
      accent: "#DC2626",
    },
  ];

  const commonChart = {
    chart: {
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif",
    },
    dataLabels: { enabled: false },
    grid: { borderColor: "#EEF2F6" },
    xaxis: {
      categories: daily.labels || [],
      labels: { style: { colors: "#6B7280", fontSize: "11px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: "#6B7280", fontSize: "11px" } },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      markers: { radius: 4 },
    },
    tooltip: { theme: "light" },
  };

  const growthOptions = {
    ...commonChart,
    colors: ["#16A34A", "#2563EB"],
    stroke: { curve: "smooth", width: 3 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 0.8, opacityFrom: 0.3, opacityTo: 0.05 },
    },
  };

  const engagementOptions = {
    ...commonChart,
    colors: ["#EF4444", "#2563EB", "#F59E0B"],
    plotOptions: {
      bar: {
        borderRadius: 5,
        columnWidth: "45%",
      },
    },
  };

  const regionOptions = {
    chart: {
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif",
    },
    colors: ["#16A34A"],
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 5,
        barHeight: "50%",
      },
    },
    dataLabels: { enabled: false },
    grid: { borderColor: "#EEF2F6" },
    xaxis: {
      categories: topRegions.map((item) => item.state || "Unknown"),
      labels: { style: { colors: "#6B7280", fontSize: "11px" } },
    },
    yaxis: {
      labels: { style: { colors: "#2F3A4A", fontSize: "11px" } },
    },
  };

  const growthSeries = [
    { name: "Users", data: daily.users || [] },
    { name: "Posts", data: daily.posts || [] },
  ];

  const engagementSeries = [
    { name: "Likes", data: engagement.likes || [] },
    { name: "Comments", data: engagement.comments || [] },
    { name: "Saves", data: engagement.saves || [] },
  ];

  const regionSeries = [
    { name: "Users", data: topRegions.map((item) => item.total || 0) },
  ];

  return (
    <div className="admin-dashboard-v2">
      <div className="dashboard-hero">
        <div>
          <p className="dashboard-eyebrow">Live overview</p>
          <h2>Mansthal admin dashboard</h2>
          <p className="dashboard-hero-subtitle">
            Users, posts, engagement, reports, and feedback refreshed from backend data.
          </p>
        </div>
        <div className="dashboard-refresh-area">
          <p>Updated {dashboardData?.generated_at ? formatIstDateTime(dashboardData.generated_at) + " IST" : "-"}</p>
          <button onClick={fetchDashboard} disabled={showloding}>
            {showloding ? <Spinner color="#ffffff" size={14} /> : <FontAwesomeIcon icon={faChartLine} />}
            Refresh
          </button>
        </div>
      </div>

      <div className="dashboard-kpi-grid">
        {kpiCards.map((card) => (
          <div className="dashboard-kpi-card" key={card.title}>
            <div className="dashboard-kpi-icon" style={{ backgroundColor: card.color, color: card.accent }}>
              <FontAwesomeIcon icon={card.icon} />
            </div>
            <p className="dashboard-kpi-value">{formatNumber(card.value)}</p>
            <p className="dashboard-kpi-title">{card.title}</p>
            <p className="dashboard-kpi-trend">{card.trend}</p>
          </div>
        ))}
      </div>

      <div className="dashboard-grid-main">
        <div className="dashboard-panel dashboard-panel-wide">
          <div className="dashboard-panel-header">
            <div>
              <p className="dashboard-panel-title">Growth Trend</p>
              <p className="dashboard-panel-subtitle">New users and posts over the last 7 days</p>
            </div>
          </div>
          <Chart options={growthOptions} series={growthSeries} type="area" height={300} />
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <div>
              <p className="dashboard-panel-title">Moderation Queue</p>
              <p className="dashboard-panel-subtitle">Open safety signals</p>
            </div>
          </div>
          <div className="dashboard-moderation-list">
            <div>
              <span><FontAwesomeIcon icon={faFlag} /></span>
              <p>{formatNumber(moderation.pending_reports)}</p>
              <small>Pending reports</small>
            </div>
            <div>
              <span><FontAwesomeIcon icon={faBan} /></span>
              <p>{formatNumber(moderation.blocked_users)}</p>
              <small>Blocked users</small>
            </div>
            <div>
              <span><FontAwesomeIcon icon={faCommentDots} /></span>
              <p>{formatNumber(moderation.reported_comments)}</p>
              <small>Reported comments</small>
            </div>
          </div>
        </div>

        <div className="dashboard-panel dashboard-panel-wide">
          <div className="dashboard-panel-header">
            <div>
              <p className="dashboard-panel-title">Engagement</p>
              <p className="dashboard-panel-subtitle">Likes, comments, and saves over the last 7 days</p>
            </div>
          </div>
          <Chart options={engagementOptions} series={engagementSeries} type="bar" height={280} />
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <div>
              <p className="dashboard-panel-title">Top Regions</p>
              <p className="dashboard-panel-subtitle">Users by state</p>
            </div>
          </div>
          {topRegions.length > 0 ? (
            <Chart options={regionOptions} series={regionSeries} type="bar" height={280} />
          ) : (
            <p className="dashboard-empty-text">No region data yet</p>
          )}
        </div>
      </div>

      <div className="dashboard-bottom-grid">
        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <div>
              <p className="dashboard-panel-title">Latest Posts</p>
              <p className="dashboard-panel-subtitle">Recent user activity</p>
            </div>
          </div>
          <div className="dashboard-list">
            {latestPosts.map((post) => (
              <div className="dashboard-list-row" key={post.id}>
                <div>
                  <p>{post.fullname || "Unknown user"}</p>
                  <small>{post.biotext || "Media post"}</small>
                </div>
                <span className="dashboard-row-actions">
                  {post.created_at ? moment(post.created_at).fromNow() : "-"}
                  <button title="View post" onClick={() => setSelectedPost(post)}>
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                </span>
              </div>
            ))}
            {latestPosts.length === 0 ? <p className="dashboard-empty-text">No posts yet</p> : null}
          </div>
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <div>
              <p className="dashboard-panel-title">Recent Reports</p>
              <p className="dashboard-panel-subtitle">Needs admin review</p>
            </div>
          </div>
          <div className="dashboard-list">
            {recentReports.map((report) => (
              <div className="dashboard-list-row" key={report.id}>
                <div>
                  <p>{report.reporter_name || "Unknown reporter"}</p>
                  <small>{report.reason || "No reason added"}</small>
                </div>
                <span>{report.created_at ? formatIstDate(report.created_at) : "-"}</span>
              </div>
            ))}
            {recentReports.length === 0 ? <p className="dashboard-empty-text">No pending reports</p> : null}
          </div>
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-panel-header">
            <div>
              <p className="dashboard-panel-title">Latest Feedback</p>
              <p className="dashboard-panel-subtitle">Recent ratings from users</p>
            </div>
          </div>
          <div className="dashboard-list">
            {recentFeedback.map((feedback) => (
              <div className="dashboard-list-row" key={feedback.id}>
                <div>
                  <p>{feedback.fullname || "Unknown user"}</p>
                  <small>{feedback.feedback || "No feedback text"}</small>
                </div>
                <span><FontAwesomeIcon icon={faStar} /> {feedback.rating || "-"}</span>
              </div>
            ))}
            {recentFeedback.length === 0 ? <p className="dashboard-empty-text">No feedback yet</p> : null}
          </div>
        </div>
      </div>
      <PostPreviewModal post={selectedPost} onClose={() => setSelectedPost(null)} />
    </div>
  );
};

export default Dashboard;
